import { cn } from '@/lib/utils';
import { PropsWithChildren, ReactNode } from 'react';

/*
 * The chapter spine — a numbered rail the About pages hang their content from.
 *
 * These pages are the only ones on the site telling a story in order: the
 * academy's growth, the founder's journey, a Rotary tenure, a list of awards by
 * year. A rail with numbered stops says "this has a beginning and an end" in a
 * way a grid of cards cannot, and it is built from the site's own running
 * stitch rather than a motif imported for the occasion.
 *
 * The rail is one continuous line behind the markers, which mask it with their
 * own background — so `Spine` belongs on a `raised` band, whose bg-background
 * the markers match. Pass `markerClassName` to sit it on any other surface.
 */
const Spine = ({
  children,
  className,
}: PropsWithChildren<{ className?: string }>) => (
  <div className={cn('relative', className)}>
    {/* Stops short at both ends so the line does not appear to run out of the
        section — it starts at the first marker and dies after the last. */}
    <div
      aria-hidden
      className="seam-vertical absolute bottom-16 left-4 top-4 md:bottom-24 md:left-6"
    />

    <ol className="flex flex-col">{children}</ol>
  </div>
);

const SpineItem = ({
  marker,
  children,
  className,
  markerClassName,
}: PropsWithChildren<{
  /** The stop's label — a chapter number, or a year on the awards timeline. */
  marker: ReactNode;
  className?: string;
  markerClassName?: string;
}>) => (
  <li className={cn('relative pb-14 pl-12 md:pb-20 md:pl-24', className)}>
    <span
      aria-hidden
      className={cn(
        'absolute left-0 top-0 inline-flex size-8 items-center justify-center rounded-full border bg-background font-display text-xs font-semibold tabular-nums text-primary md:size-12 md:text-sm',
        markerClassName
      )}
    >
      {marker}
    </span>

    {children}
  </li>
);

export { Spine, SpineItem };
